import { get, on } from "@bigyouth/base";

import pressedKey from "../utils/pressedKey";
import removeAttributes from "../utils/removeAttributes";
import scrollManager from "../utils/scrollManager";
import setAttributes from "../utils/setAttributes";
import { create as createPipe } from "../utils/sigpipe";
import { getAllTabbable, getTabbable } from "../utils/tabbable";

export const ACTIONS = {
  CLOSE: "MODAL_CLOSE",
  OPEN: "MODAL_OPEN",
};

const CONTENT_SELECTOR = ".app";

/**
 * Modal will send the corresponding dom node in the .modals layer and create a pipe for future
 * interactions.
 *
 * You can get the pipe with the id injected as an attribute on the dom node (data-pipe-id) and
 * control the modal with the actions specified above. The pipe expects an object with the following
 * attributes:
 * action(string, required): One of the values of ACTIONS.
 * emitterEl(domNode, optional): The dom node that opened the modal if there is one.
 *
 * A Modal can be controlled directly with a ModalControl or programmatically (exemple in FooForm).
 */
export default {
  name: "modal",

  ui: {
    "closeButton[]": "[js-modal-close]",
  },

  events: [
    {
      on: "click",
      target: "closeButton",
      handle: "handleClose",
    },
    {
      on: "click",
      handle(e) {
        // if click on overlay, close modal
        if (e.target === this.el) {
          this.handleClose();
          e.preventDefault();
        }
      },
    },
  ],

  componentDidMount() {
    this.mainContent = get(CONTENT_SELECTOR);

    get(".modals").appendChild(this.el);

    // Create pipe for future communications
    this.pipe = createPipe();
    setAttributes({ dataPipeId: `${this.pipe.id}` })(this.el);

    this.pipe.on(({ action, emitterEl }) => {
      this.emitterEl = emitterEl;

      switch (action) {
        case ACTIONS.OPEN:
          this.handleOpen();
          break;

        case ACTIONS.CLOSE:
          this.handleClose();
          break;
      }
    });

    // Accessibility - Contains tab in modal
    this.beforeBlocker = document.createElement("span");
    this.afterBlocker = this.beforeBlocker.cloneNode(true);

    this.offEscape = on("keyup", (event) => {
      if (pressedKey("esc")(event)) {
        this.handleClose();
      }
    })(document.body);

    this.offBeforeBlock = on("focus", () => {
      const tabbables = getAllTabbable(this.el);
      tabbables[tabbables.length - 2].focus({ preventScroll: true });
    })(this.beforeBlocker);

    this.offAfterBlock = on("focus", () => {
      getTabbable(this.el).focus({ preventScroll: true });
    })(this.afterBlocker);

    this.el.parentNode.insertBefore(this.beforeBlocker, this.el);
    this.el.appendChild(this.afterBlocker);
  },

  componentWillUnmount() {
    this.pipe.destroy();

    this.beforeBlocker.remove();
    this.afterBlocker.remove();

    this.offEscape();
    this.offBeforeBlock();
    this.offAfterBlock();

    removeAttributes("js-sr-inactive")(this.mainContent);
  },

  handleOpen() {
    if (!this.el.classList.contains("is-hidden")) return;

    this.el.classList.remove("is-hidden");

    // Disabled scrolling on main content
    setTimeout(scrollManager.stop, 0);

    // Accessibility - Enable the modal and disabled main content on screen readers
    removeAttributes("js-sr-inactive")(this.el);
    setAttributes({ "js-sr-inactive": "" })(this.mainContent);

    // Accessibility - Set the focus on the modal close button
    this.ui.closeButton[0].focus({ preventScroll: true });
  },

  handleClose() {
    if (this.el.classList.contains("is-hidden")) return;

    this.el.classList.add("is-hidden");

    if (this.el.querySelector("[js-video-player-video]"))
      this.el.querySelector("[js-video-player-video]").pause();

    // Accessibility - Disabled the modal and enable main content on screen readers
    setAttributes({ "js-sr-inactive": "" })(this.el);
    removeAttributes("js-sr-inactive")(this.mainContent);

    // Enable scrolling on main content
    scrollManager.restart();

    // Accessibility - Set the focus on the emitter that opened the modal if it exists
    if (this.emitterEl) this.emitterEl.focus({ preventScroll: true });
  },
};
